import React, { useMemo } from "react";
import { Draggable, DraggableProvided, DraggableStateSnapshot, Droppable } from "react-beautiful-dnd";

import AddIcon from "@mui/icons-material/Add";
import AdjustIcon from "@mui/icons-material/Adjust";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import { IconButton, Stack, StackProps, Typography } from "@mui/material";
import { AutoplayIcon, BigPlusIcon, HoldIcon, ProgressLoaderIcon } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { Menu } from "components/popups";
import { startCase } from "lodash";
import { useDealStatusesSelector, usePickUserPermissions, useUserRoleSelector } from "store";
import { DealStage, IStatus, UpdateDealStageData } from "store/types";

import { RoleName } from "../../../../constants";
import { PipelineCard } from "./PipelineCard";

const DEFAULT_COLOR = "#195396";

interface Props extends StackProps {
  onDeleteColumn: (stage: DealStage) => void;
  onRenameColumn: (stage: DealStage) => void;
  updateColumn: (data: UpdateDealStageData) => void;
  stage: DealStage;
  draggableProvided: DraggableProvided;
  draggableSnapshot: DraggableStateSnapshot;
  onCreateDeal: (stageId: number) => void;
  isFirstColumn?: boolean;
  allColumnsEmpty?: boolean;
}

const defaultColumnSx: StackProps["sx"] = {
  width: "219px",
  minWidth: "219px",
  maxWidth: "219px",
  borderRadius: "8px",
  bgcolor: "white",
  boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)",
  p: "12px",
  gap: "12px"
};

export const PipelineColumn: React.FC<Props> = ({
  onRenameColumn,
  stage,
  draggableProvided,
  draggableSnapshot,
  onCreateDeal,
  isFirstColumn = false,
  allColumnsEmpty = false
}) => {
  const { deals, id, title, deals_count, status_id } = stage;
  const statuses = useDealStatusesSelector();
  const role = useUserRoleSelector();
  const myDeals = usePickUserPermissions("deals");
  const isViewerPermissionDeals = myDeals && myDeals === "viewer";

  const status: IStatus | undefined = useMemo(() => {
    return statuses.find((item) => item.id === status_id);
  }, [statuses, status_id]);

  const columnHeight = useMemo(() => {
    if (!isFirstColumn && allColumnsEmpty) return "48px";
    if (!deals.length && !allColumnsEmpty) return "84px";
    return undefined; // Let it size naturally based on content
  }, [deals.length, isFirstColumn, allColumnsEmpty]);

  // Get status icon based on status_id
  const getStatusIcon = () => {
    const iconProps = {
      sx: { width: "16px", height: "16px", color: status?.color || DEFAULT_COLOR }
    };

    switch (status_id) {
      case 1: // New
        return <AdjustIcon {...iconProps} />;
      case 2: // In progress
        return <ProgressLoaderIcon {...iconProps} />;
      case 3: // On hold
        return <HoldIcon {...iconProps} />;
      case 4: // Invested
        return <AutoplayIcon {...iconProps} />;
      case 5: // Declined
        return <BlockIcon {...iconProps} />;
      case 6: // Closed
        return <CheckCircleIcon {...iconProps} />;
      case 7: // Hidden
        return <VisibilityOffIcon {...iconProps} />;
      case 8: // Altius Opportunity
        return <WorkspacesIcon {...iconProps} />;
      case 9: // Shared with me
        return <ShareIcon {...iconProps} />;
      default:
        return <AdjustIcon {...iconProps} />;
    }
  };

  return (
    <Stack
      sx={{
        ...defaultColumnSx,
        ...draggableProvided.draggableProps.style,
        ...(draggableSnapshot.draggingOver && { border: "1px dashed #5d7782" }),
        ...(columnHeight && { height: columnHeight }),
        alignSelf: "flex-start" // Ensure column only takes height it needs
      }}
      {...draggableProvided.dragHandleProps}
      {...draggableProvided.draggableProps}
      ref={draggableProvided.innerRef}
    >
      {/* Column Header */}
      <Stack
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          bgcolor: "white",
          borderRadius: "8px",
          overflow: "hidden"
        }}
      >
        <Stack direction="row" gap="10px" alignItems="center" overflow="hidden" flex={1} minWidth={0}>
          {/* Status Indicator with icon */}
          <Stack direction="row" gap="4px" alignItems="center" flexShrink={0}>
            {getStatusIcon()}
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                color: status?.color || DEFAULT_COLOR,
                lineHeight: "20px",
                whiteSpace: "nowrap"
              }}
            >
              {startCase(title)}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 400,
              color: "#92a7bc",
              lineHeight: "20px",
              flexShrink: 0
            }}
          >
            {deals_count}
          </Typography>
        </Stack>
        <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermissionDeals}>
          <Stack direction="row" gap="5px" alignItems="center" flexShrink={0}>
            {!stage.is_system && (
              <IconButton
                onClick={() => onCreateDeal(id)}
                sx={{
                  width: "24px",
                  height: "24px",
                  padding: "4px",
                  borderRadius: "32px",
                  "&:hover": {
                    bgcolor: "transparent"
                  }
                }}
              >
                <AddIcon sx={{ fontSize: "16px", color: "#071F35" }} />
              </IconButton>
            )}
            <Menu
              icon={<MoreVertIcon sx={{ fontSize: "16px", color: "#071F35" }} />}
              iconButtonProps={{
                sx: {
                  width: "24px",
                  height: "24px",
                  padding: "4px",
                  borderRadius: "32px"
                }
              }}
              options={[
                { title: "Rename column", onClick: () => onRenameColumn(stage) },
                {
                  title: "Delete column",
                  disabled: !!deals.length,
                  onClick: () => {
                    // Handle delete
                  }
                }
              ]}
            />
          </Stack>
        </RoleComponentGuard>
      </Stack>

      {/* Column Content */}
      {(!!deals.length || (isFirstColumn && allColumnsEmpty)) && (
        <Droppable droppableId={`stage-${id}`} type="STAGE">
          {(provided, dragState) => (
            <Stack
              sx={{ flexGrow: 1, bgcolor: dragState.isDraggingOver ? "white" : "transparent" }}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {(!!deals.length || (!deals.length && isFirstColumn)) && (
                <Stack
                  sx={{
                    gap: "12px",
                    cursor: "default !important",
                    flexGrow: 0,
                    bgcolor: "white",
                    p: 0
                  }}
                >
                  {isFirstColumn && allColumnsEmpty && !deals.length && (
                    <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermissionDeals}>
                      <Stack
                        sx={{ p: "8px", bgcolor: "#f6f8fc", borderRadius: "8px", cursor: "pointer" }}
                        className="scale-click"
                        onClick={() => onCreateDeal(id)}
                      >
                        <Stack flexDirection="row" alignItems="center" gap="10px">
                          <IconButton
                            size="sm"
                            sx={{
                              bgcolor: "#071F35",
                              color: "#FFFFFF",
                              ":hover": { bgcolor: "#071F35", color: "#FFFFFF" }
                            }}
                          >
                            <BigPlusIcon />
                          </IconButton>
                          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>Add deal</Typography>
                        </Stack>
                      </Stack>
                    </RoleComponentGuard>
                  )}
                  {deals.map((item, index) => (
                    <Draggable
                      isDragDisabled={role?.name === RoleName.TeamViewer || isViewerPermissionDeals}
                      key={`${id}-${item.id}`}
                      draggableId={`${id}-${item.id}`}
                      index={index}
                    >
                      {(draggableProvided) => (
                        <PipelineCard {...item} draggableProvided={draggableProvided} stageId={id} />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Stack>
          )}
        </Droppable>
      )}
      <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermissionDeals}>
        {!allColumnsEmpty && !stage.is_system && (
          <Stack sx={{ cursor: "pointer" }} className="scale-click">
            <Stack flexDirection="row" alignItems="center" gap="10px" onClick={() => onCreateDeal(id)}>
              <AddIcon sx={{ fontSize: "16px", color: "#071F35" }} />
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#0a3142" }}>Add deal</Typography>
            </Stack>
          </Stack>
        )}
      </RoleComponentGuard>

      {/* Add Deal Button at Bottom - Only show in first column when all columns are empty */}
    </Stack>
  );
};
